import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AnalyticsProvider } from 'shared/context/AnalyticsContext';
import AntdThemeBridge from 'shared/theme/AntdThemeBridge';
import MuiThemeBridge from 'shared/theme/MuiThemeBridge';
import { ThemeProvider as TrodoThemeProvider } from 'shared/theme/ThemeContext';
import { applyTrodoPrimaryCssVars } from 'shared/theme/trodoColors';
import { setupGlobalErrorHandler } from 'shared/utils/errorHandler';
import { initPosthog } from 'shared/utils/posthogClient';
import suppressResizeObserverErrors from 'shared/utils/resizeObserverFix';
import App from './App';
import './index.css';

// Production: keep warn/error only (same policy as backend_sdk / backendapp).
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_VERBOSE !== 'true') {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
}

applyTrodoPrimaryCssVars();

suppressResizeObserverErrors();
setupGlobalErrorHandler();
initPosthog();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Great UX for back/forward navigation: show cached data instantly
      staleTime: 60_000, // 1 min
      gcTime: 10 * 60_000, // 10 min
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function bootstrap() {
  // The app no longer runs Trodo's own browser SDK on itself: no init, no
  // autocaptured events, nothing queued on page load.
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <TrodoThemeProvider>
        {/* Both read the resolved theme from TrodoThemeProvider, so both must
            sit inside it. */}
        <MuiThemeBridge>
          <AntdThemeBridge>
            <QueryClientProvider client={queryClient}>
              <AnalyticsProvider>
                <App />
              </AnalyticsProvider>
            </QueryClientProvider>
          </AntdThemeBridge>
        </MuiThemeBridge>
      </TrodoThemeProvider>
    </React.StrictMode>
  );
}

bootstrap();
